import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {Route, Switch} from "react-router-dom";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, Title} from "@khanacademy/wonder-blocks-typography";
import LoginRequired from "@repo/component-screens/login-required";
import {PermissionsInfo} from "@repo/shared-core";
import {mediaQueries} from "@repo/shared-styles";
import {EmailSettings, LinkedAccountMessageBanner} from "@repo/user-settings";

import NotFound from "#app-shell-package/errors/not-found.tsx";
import PageRoot from "#app-shell-package/page-root.tsx";
import MaybeReturnLink from "#components/maybe-return-link-package/maybe-return-link.tsx";
import Shimmer, {Skeleton} from "#components/shimmer-package/shimmer.tsx";
import TabbedPanel, {
    type Tab,
    type TabHeader,
} from "#components/tabbed-panel-package/tabbed-panel.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withRouter, {
    type WithoutRouter,
    type WithRouterProps,
} from "#data-access/with-router.ts";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";

import AccountSettings from "./account-settings.tsx";
import KhanLabsSettings from "./khan-labs-settings.tsx";
import LinkEmail from "./link-email.tsx";
import {withStrings} from "./locales/settings-page/load-strings.tsx";
import {SettingsPageQuery} from "./settings-page-query.tsx";

const TAB_PATH_MAP: Partial<Record<SettingsPageTabId, string>> = {
    account: "/settings/account",
    email: "/settings/email",
    khanmigo: "/settings/khanmigo",
};

const strings = {
    settingsFor: (nickname: string) => t`Settings for ${nickname}`,

    warningsChangingSettings: (email: string) =>
        t`Heads up! You are changing email settings for  ${email}`,
} as const;

type SettingsPageTabId = "account" | "email" | "khanmigo";

type Props = WithRouterProps & WithLocationProps;

type State = {
    targetKaid: string | null | undefined;
    unsubToken: string | null | undefined;
};

class SettingsPage extends React.Component<Props, State> {
    state: State = {
        targetKaid: this.props.location.queryParams.kaid,
        unsubToken: this.props.location.queryParams.t1c,
    };

    getTabs(
        isUnsubForOtherUser: boolean,
        isEnrolledInKhanLabs: boolean,
        targetIsChild?: boolean | null,
        targetKaid?: string | null,
    ): Array<Tab<SettingsPageTabId>> {
        // targetKaid is passed in from the settings page query instead of taken
        // from state because the state kaid only exists if it's a url parameter,
        // wheread the query will always return the target kaid.
        const {unsubToken} = this.state;

        const tabs: Array<Tab<SettingsPageTabId>> = [];
        // We only don't render the account tab in cases where a one click
        // unsubscribe token is passed in and it's not for the currect user.
        if (!isUnsubForOtherUser && targetKaid) {
            const accountTab = {
                key: "account",
                renderTab: (isTabSelected: boolean, props: any) => {
                    const tabToRender: TabHeader = {
                        tabHeader: (
                            <span
                                className={css(
                                    styles.tab,
                                    isTabSelected && styles.activeTab,
                                )}
                                {...props}
                            >
                                <LabelLarge>{t`Account`}</LabelLarge>
                            </span>
                        ),
                        isTabDisabled: false,
                    };
                    return tabToRender;
                },
                renderContent: () => (
                    <AccountSettings targetKaid={targetKaid} />
                ),
            } as const;
            tabs.push(accountTab);
        }
        // We show the email tab if the target isn't a child or if there's
        // an unsubscribe token for a different account.
        if (!targetIsChild || isUnsubForOtherUser) {
            const emailTab = {
                key: "email",
                renderTab: (isTabSelected: boolean, props: any) => {
                    const tabToRender: TabHeader = {
                        tabHeader: (
                            <span
                                className={css(
                                    styles.tab,
                                    isTabSelected && styles.activeTab,
                                )}
                                {...props}
                            >
                                <LabelLarge>{t`Email`}</LabelLarge>
                            </span>
                        ),
                        isTabDisabled: false,
                    };
                    return tabToRender;
                },
                renderContent: () => (
                    <EmailSettings
                        // since targetKaid defaults to the current user, we
                        // only want to pass this if we're not unsubscribing
                        // from a different account
                        targetKaid={isUnsubForOtherUser ? null : targetKaid}
                        token={unsubToken}
                    />
                ),
            } as const;
            tabs.push(emailTab);
        }
        if (targetKaid && !isUnsubForOtherUser && isEnrolledInKhanLabs) {
            const khanmigoTab = {
                key: "khanmigo",
                renderTab: (isTabSelected: boolean, props: any) => {
                    const tabToRender: TabHeader = {
                        tabHeader: (
                            <span
                                className={css(
                                    styles.tab,
                                    isTabSelected && styles.activeTab,
                                )}
                                {...props}
                            >
                                <LabelLarge>{t`Khanmigo`}</LabelLarge>
                            </span>
                        ),
                        isTabDisabled: false,
                    };
                    return tabToRender;
                },
                renderContent: () => (
                    <KhanLabsSettings targetKaid={targetKaid} />
                ),
            } as const;
            tabs.push(khanmigoTab);
        }
        return tabs;
    }

    onTabChanged: (
        newTabId: SettingsPageTabId,
        oldTabId: SettingsPageTabId,
    ) => void = (newTabId: SettingsPageTabId, oldTabId: SettingsPageTabId) => {
        const {
            location: {search},
            history,
        } = this.props;
        if (newTabId !== oldTabId) {
            const path = TAB_PATH_MAP[newTabId];
            history.push(`${path}${search}`);
        }
    };

    renderWarningBanner(emailToUnsubscribe: string): React.ReactNode {
        return (
            <View style={[styles.banner, styles.error]}>
                <View style={styles.bannerTextContainer}>
                    <LabelLarge>
                        {strings.warningsChangingSettings(emailToUnsubscribe)}
                    </LabelLarge>
                </View>
            </View>
        );
    }

    renderMaybeReturnLink(): React.ReactNode {
        return <MaybeReturnLink style={styles.returnLink} />;
    }

    renderSettingsPage(tabId: SettingsPageTabId): React.ReactNode {
        const {targetKaid, unsubToken} = this.state;
        const {location} = this.props;
        return (
            <SettingsPageQuery targetKaid={targetKaid} token={unsubToken}>
                {({target, emailToUnsubscribe, isLoading, isError}) => {
                    if (isLoading || isError) {
                        return (
                            <View
                                style={[
                                    styles.settingsContainer,
                                    styles.titleAndWarning,
                                ]}
                            >
                                <Shimmer>
                                    <Skeleton width={140} height={40} />
                                </Shimmer>
                                <Strut size={spacing.small_12} />
                                <Shimmer>
                                    <Skeleton width={120} height={35} />
                                </Shimmer>
                                <Strut size={spacing.xLarge_32} />
                                <CircularSpinner
                                    size="medium"
                                    style={styles.spinner}
                                />
                            </View>
                        );
                    }

                    const isUnsubForOtherUser =
                        !target ||
                        (!!emailToUnsubscribe &&
                            emailToUnsubscribe !== target.email);

                    const isEnrolledInKhanLabs = !!target?.isEnrolledInAIGuide;

                    return (
                        <TabbedPanel
                            id="settings-tabs"
                            tabListAriaLabel={t`Settings`}
                            tabs={this.getTabs(
                                isUnsubForOtherUser,
                                isEnrolledInKhanLabs,
                                target && target.isChild,
                                target && target.id,
                            )}
                            selectedTabKey={tabId}
                            onTabChanged={(newTabId: SettingsPageTabId) =>
                                this.onTabChanged(newTabId, tabId)
                            }
                        >
                            {(renderTabs, renderContentPanel) => (
                                <View style={styles.settingsContainer}>
                                    <View style={styles.titleAndWarning}>
                                        {isUnsubForOtherUser &&
                                            emailToUnsubscribe &&
                                            this.renderWarningBanner(
                                                emailToUnsubscribe,
                                            )}
                                        <LinkedAccountMessageBanner
                                            messageKey={
                                                location?.queryParams
                                                    ?.messageKey
                                            }
                                            email={
                                                location?.state
                                                    ? location.state.email
                                                    : null
                                            }
                                        />
                                        {this.renderMaybeReturnLink()}
                                        <Title>
                                            {target && !target.isActor
                                                ? strings.settingsFor(
                                                      target.nickname ||
                                                          target.username ||
                                                          "",
                                                  )
                                                : t`Settings`}
                                        </Title>
                                    </View>
                                    <Strut size={spacing.small_12} />
                                    {!isUnsubForOtherUser &&
                                        target &&
                                        !target.isChild &&
                                        renderTabs(
                                            styles.tabPanel,
                                            styles.tabItemSpacing,
                                        )}
                                    {renderContentPanel(styles.contentPanel)}
                                </View>
                            )}
                        </TabbedPanel>
                    );
                }}
            </SettingsPageQuery>
        );
    }

    render(): React.ReactNode {
        return (
            <PageRoot settings={{title: t`Settings`}}>
                <Switch>
                    <Route
                        path="/settings/account"
                        render={() => {
                            if (PermissionsInfo.isLoggedIn) {
                                return this.renderSettingsPage("account");
                            }

                            return <LoginRequired />;
                        }}
                    />
                    <Route
                        path="/settings/email"
                        render={({location}) => {
                            // The only case in which we show a not-logged-in
                            // user a settings page is if they have an
                            // unsubscribe email token.
                            const params = new URLSearchParams(location.search);
                            if (
                                PermissionsInfo.isLoggedIn ||
                                params.get("t1c")
                            ) {
                                return this.renderSettingsPage("email");
                            }

                            return <LoginRequired />;
                        }}
                    />
                    <Route
                        path="/settings/linkaccount"
                        render={() => {
                            if (PermissionsInfo.isLoggedIn) {
                                return <LinkEmail />;
                            }
                            return <LoginRequired />;
                        }}
                    />
                    <Route
                        path="/settings/khanmigo"
                        render={() => {
                            if (PermissionsInfo.isLoggedIn) {
                                return this.renderSettingsPage("khanmigo");
                            }
                            return <LoginRequired />;
                        }}
                    />
                    {/* for any links out in the wild */}
                    <SmartRedirect
                        from="/settings/khan-labs"
                        to="/settings/khanmigo"
                    />
                    <Route path="/settings/*">
                        <NotFound />
                    </Route>
                </Switch>
            </PageRoot>
        );
    }
}

const styles = StyleSheet.create({
    tab: {
        height: 35,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "none",
        border: "none",
        borderBottom: "3px solid transparent",
        // cursor: "pointer",
        padding: 0,
        ":hover": {
            borderColor: color.offBlack16,
        },
    },
    tabItemSpacing: {
        ":not(:last-child)": {
            marginRight: spacing.large_24,
        },
    },
    activeTab: {
        color: color.blue,
        borderColor: color.blue,
        // cursor: "default",
        ":hover": {
            borderColor: color.blue,
        },
    },
    banner: {
        flexDirection: "row",
        padding: spacing.medium_16,
        marginBottom: spacing.medium_16,
        borderRadius: 6,
        color: color.white,
    },
    error: {
        backgroundColor: color.red,
    },
    bannerTextContainer: {
        paddingRight: spacing.medium_16,
    },
    contentPanel: {
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
    tabPanel: {
        paddingBottom: spacing.xLarge_32,
        paddingLeft: spacing.medium_16,
    },
    settingsContainer: {
        marginTop: spacing.xxLarge_48,
        marginBottom: spacing.medium_16,
        marginLeft: "auto",
        marginRight: "auto",
        width: 640,
        [mediaQueries.smOrSmaller]: {
            width: "auto",
            marginLeft: spacing.medium_16,
            marginRight: spacing.medium_16,
        },
    },
    titleAndWarning: {
        paddingLeft: spacing.medium_16,
    },
    returnLink: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingBottom: spacing.xSmall_8,
    },
    spinner: {
        alignItems: "center",
    },
});

type ExportProps = WithoutRouter<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof SettingsPage,
            React.ComponentProps<typeof SettingsPage>
        >
    >
>;

export default compose(
    withStrings,
    withRouter,
    withLocation,
)(SettingsPage) as React.ComponentType<ExportProps>;
